import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaSearch, FaExclamationTriangle, FaSave, FaTimes } from 'react-icons/fa';
import axios from 'axios';
import { ENDPOINT } from '../api/auth';
import './LowStockManagement.css';

const LowStockManagement = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [lowStockThreshold, setLowStockThreshold] = useState('');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  // Fetch all categories
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        ENDPOINT,
        {},
        {
          params: { action: 'getCategories' },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.data.success) {
        setCategories(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch all products with their current stock levels
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        ENDPOINT,
        {},
        {
          params: { action: 'getProductsLowStock' },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.data.success) {
        setProducts(response.data.products || []);
        // Don't set filteredProducts here, let the useEffect handle it
      } else {
        toast.error('Failed to fetch products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Error fetching products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch low stock products
  const fetchLowStockProducts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        ENDPOINT,
        {},
        {
          params: { action: 'getLowStockProducts' },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.data.success) {
        setProducts(response.data.products || []);
        setFilteredProducts(response.data.products || []);
      } else {
        toast.error('Failed to fetch low stock products');
      }
    } catch (error) {
      console.error('Error fetching low stock products:', error);
      toast.error('Error fetching low stock products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle search and category filter
  useEffect(() => {
    let filtered = [...products];
    
    // Apply search filter
    if (searchQuery.trim() !== '') {
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
          product.id.toString().includes(searchQuery)
      );
    }
    
    // Apply category filter if selected
    if (selectedCategory) {
      const selectedCatObj = categories.find(c => c.id?.toString() === selectedCategory.toString());
      const selectedCatName = selectedCatObj?.name?.toString().toLowerCase();

      filtered = filtered.filter(product => {
        const pid = product.category_id?.toString();
        const pname = product.category?.toString().toLowerCase();

        // Prefer strict ID match when product has category_id
        if (pid) {
          return pid === selectedCategory.toString();
        }
        // Fallback: match by name when product only has a category name
        if (pname && selectedCatName) {
          return pname === selectedCatName;
        }
        return false;
      });
    }
    
    setFilteredProducts(filtered);
  }, [searchQuery, products, selectedCategory, categories]);

  // Toggle between all products and low stock only
  useEffect(() => {
    if (showLowStockOnly) {
      fetchLowStockProducts();
    } else {
      fetchProducts();
    }
    // Reset selected category when toggling between views
    setSelectedCategory('');
  }, [showLowStockOnly]);

  // Initialize component
  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []); // Removed selectedCategory from dependencies to prevent unnecessary API calls

  // Handle edit button click
  const handleEditClick = (product) => {
    setEditingProduct(product);
    setLowStockThreshold(product.low_stock || '');
  };

  // Handle save button click
  const handleSaveClick = async () => {
    if (!editingProduct || lowStockThreshold === '') {
      toast.error('Please enter a valid threshold');
      return;
    }

    const threshold = parseInt(lowStockThreshold, 10);
    if (isNaN(threshold) || threshold < 0) {
      toast.error('Please enter a valid positive number');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        ENDPOINT,
        {
          action: 'updateLowStockThreshold',
          product_id: editingProduct.id,
          low_stock: threshold,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        toast.success('Low stock threshold updated successfully');
        // Refresh the products list
        if (showLowStockOnly) {
          fetchLowStockProducts();
        } else {
          fetchProducts();
        }
        setEditingProduct(null);
      } else {
        toast.error(response.data.message || 'Failed to update threshold');
      }
    } catch (error) {
      console.error('Error updating threshold:', error);
      toast.error('Error updating threshold. Please try again.');
    }
  };

  // Format number with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  return (
    <div className="low-stock-container">
      <div className="low-stock-header">
        <h2>Low Stock Management</h2>
        <div className="controls">
          <div className="search-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="toggle-container">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={showLowStockOnly}
                onChange={(e) => setShowLowStockOnly(e.target.checked)}
                className="toggle-input"
              />
              <span className="toggle-slider"></span>
              <span className="toggle-text">Show Low Stock Only</span>
            </label>
          </div>
          <div className="category-filter">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="category-select"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="products-table-container">
          <table className="products-table">
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Current Stock</th>
                <th>Low Stock Threshold</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-products">
                    {showLowStockOnly
                      ? 'No products with low stock found.'
                      : 'No products found.'}
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product) => (
                  <tr
                    key={product.id}
                    className={`${product.low_stock_alert ? 'low-stock-row' : ''} ${
                      editingProduct?.id === product.id ? 'editing' : ''
                    }`}
                  >
                    <td>
                      <div className="product-name">{product.name}</div>
                      {product.description && (
                        <div className="product-description">{product.description}</div>
                      )}
                    </td>
                    <td className="stock-cell">
                      {formatNumber(product.in_stock || 0)} units
                    </td>
                    <td>
                      {editingProduct?.id === product.id ? (
                        <input
                          type="number"
                          value={lowStockThreshold}
                          onChange={(e) => setLowStockThreshold(e.target.value)}
                          min="0"
                          className="threshold-input"
                        />
                      ) : (
                        <span className="threshold-value">
                          {product.low_stock || 0} units
                        </span>
                      )}
                    </td>
                    <td>
                      {product.low_stock_alert ? (
                        <span className="status-badge warning">
                          <FaExclamationTriangle className="icon" /> Low Stock
                        </span>
                      ) : product.low_stock > 0 ? (
                        <span className="status-badge success">In Stock</span>
                      ) : (
                        <span className="status-badge">Not Set</span>
                      )}
                    </td>
                    <td className="actions-cell">
                      {editingProduct?.id === product.id ? (
                        <div className="edit-actions">
                          <button
                            onClick={handleSaveClick}
                            className="btn-save"
                            disabled={loading}
                          >
                            <FaSave className="icon" /> Save
                          </button>
                          <button
                            onClick={() => setEditingProduct(null)}
                            className="btn-cancel"
                            disabled={loading}
                          >
                            <FaTimes className="icon" /> Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEditClick(product)}
                          className="btn-edit"
                        >
                          Set Threshold
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LowStockManagement;
